import { isValidElement, type ReactNode } from 'react';
import { Nav, Row, Col } from 'react-bootstrap';
import { flatRoutes, routeToHandler } from '@/helpers/utils';
import NavbarNavLink from './NavbarNavLink';
import { useLocation, useParams, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppContext } from '@/Main';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

type ToolRoute = {
  name: string;
  to?: string;
  disabled?: boolean;
  icon?: IconProp | ReactNode;
  badge?: { type: string; text: string };
  children?: ToolRoute[];
};

function ToolRouteIcon({ icon }: { icon?: ToolRoute['icon'] }) {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  return <FontAwesomeIcon icon={icon as IconProp} />;
}

type NavbarDropdownToolsProps = {
  tools: ToolRoute[];
};

type NavbarRootState = {
  selectedDomain: { name: string; slug?: string };
};

const NavbarDropdownTools = ({ tools }: NavbarDropdownToolsProps) => {
  const {
    config: { navbarCollapsed, showBurgerMenu },
    setConfig
  } = useAppContext();

  const handleDropdownItemClick = () => {
    if (navbarCollapsed) {
      setConfig('navbarCollapsed', !navbarCollapsed);
    }
    if (showBurgerMenu) {
      setConfig('showBurgerMenu', !showBurgerMenu);
    }
  };

  const { pathname } = useLocation();
  const { slug, domain, domain_slug, loc, lang, keyword } = useParams();
  const selectedDomain = useSelector(
    (state: NavbarRootState) => state.selectedDomain
  );

  return (
    <div>
      <Row className="m-0 p-3">
        {tools.map((m, i) =>
          m.children ? (
            <Col key={i} xs={6} xxl={3}>
              <Nav className="flex-column">
                <NavbarNavLink
                  title={
                    <h5
                      className={`d-flex fs--1 fw-bolder align-items-center gap-2 ${m.icon ? 'ms-n3' : ''}`}
                    >
                      {m.icon && <ToolRouteIcon icon={m.icon} />}
                      {m.name}
                      {m.badge && (
                        <SubtleBadge pill bg={m.badge.type} className="ms-2">
                          {m.badge.text}
                        </SubtleBadge>
                      )}
                    </h5>
                  }
                />
                {flatRoutes(m.children).map(route => (
                  <NavLink
                    state={{ path: `${pathname}`, query: null }}
                    disabled={route.disabled}
                    onClick={() => {
                      if (route.name === 'Modal') {
                        setConfig('openAuthModal', true);
                      }
                      handleDropdownItemClick();
                    }}
                    to={routeToHandler(
                      route,
                      selectedDomain,
                      domain,
                      domain_slug,
                      slug,
                      loc,
                      lang,
                      keyword
                    )}
                    key={route.name}
                    className={({ isActive }) =>
                      route.disabled
                        ? 'nav-link navLinkForceColor py-1 disabled-link'
                        : isActive && route.to !== '#!'
                          ? 'active navLinkForceColor nav-link py-1'
                          : 'nav-link navLinkForceColor py-1'
                    }
                  >
                    <span className="nav-link-text">{route.name}</span>
                    {route.badge && (
                      <SubtleBadge pill bg={route.badge.type} className="ms-2">
                        {route.badge.text}
                      </SubtleBadge>
                    )}
                  </NavLink>
                ))}
              </Nav>
            </Col>
          ) : (
            <Col key={i} xs={6} xxl={3}>
              <Nav className="flex-column">
                <NavbarNavLink
                  title={
                    <h5
                      className={`d-flex fs--1 fw-bolder align-items-center gap-2 ${m.icon ? 'ms-n3' : ''}`}
                    >
                      {m.icon && <ToolRouteIcon icon={m.icon} />}
                      {m.name}
                    </h5>
                  }
                />
                <NavLink
                  className={({ isActive }) =>
                    m.disabled
                      ? 'nav-link navLinkForceColor py-1 disabled-link'
                      : isActive && m.to !== '#!'
                        ? 'navLinkForceColor active nav-link py-1'
                        : 'nav-link navLinkForceColor py-1'
                  }
                  to={routeToHandler(
                    m,
                    selectedDomain,
                    domain,
                    domain_slug,
                    slug,
                    loc,
                    lang,
                    keyword
                  )}
                >
                  {m.name}
                  {m.badge && (
                    <SubtleBadge pill bg={m.badge.type} className="ms-2">
                      {m.badge.text}
                    </SubtleBadge>
                  )}
                </NavLink>
              </Nav>
            </Col>
          )
        )}
      </Row>
    </div>
  );
};

export default NavbarDropdownTools;
